import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Topbar } from "@/components/parcelio/topbar";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  directoryApi,
  notificationsApi,
  vehiclesApi,
  type PersonalDirectoryEntry,
  type Notification,
  type Vehicle,
} from "@/lib/api";
import { timeAgo, cn } from "@/lib/utils";
import { ArrowLeft, UserRound, Truck, Activity as ActivityIcon } from "lucide-react";
import { toast } from "sonner";
import { LoadingBlock } from "@/components/ui/spinner";
import { useAuth } from "@/stores/auth";

const statusTone: Record<string, string> = {
  active: "bg-success/10 text-success border-success/20",
  suspended: "bg-destructive/10 text-destructive border-destructive/20",
};

export default function PersonalDashboard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const token = useAuth((s) => s.session?.accessToken);
  const [person, setPerson] = useState<PersonalDirectoryEntry | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [activity, setActivity] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const p = await directoryApi.personalOne(id, token);
        if (cancelled) return;
        setPerson(p);

        const [allVehicles, feed] = await Promise.all([
          vehiclesApi.list(token),
          notificationsApi.list(id, token).catch(() => []),
        ]);
        if (cancelled) return;
        setVehicles(allVehicles.filter((v) => v.owner_id === id));
        setActivity(feed);
      } catch {
        if (!cancelled) toast.error("Failed to load personal dashboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <>
        <Topbar title="Personal dashboard" />
        <div className="flex-1 p-4 md:p-6"><LoadingBlock label="Loading dashboard…" /></div>
      </>
    );
  }

  if (!person) {
    return (
      <>
        <Topbar title="Personal dashboard" />
        <div className="flex-1 p-8 text-center text-sm text-muted-foreground">Account not found.</div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={person.name}
        subtitle="Personal dashboard"
        actions={
          <Button variant="outline" size="sm" asChild>
            <Link to="/app/users/personal"><ArrowLeft className="mr-1.5 h-4 w-4" /> Back to personal accounts</Link>
          </Button>
        }
      />
      <div className="flex-1 space-y-4 p-4 md:p-6">
        <div className="surface-card p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-soft text-primary">
              <UserRound className="h-6 w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold">{person.name}</h2>
                <span className={cn("rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize", statusTone[person.status] ?? "bg-muted text-muted-foreground border-border")}>
                  {person.status}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">{person.email ?? "—"} · {person.phone ?? "—"}</div>
            </div>
          </div>
          <dl className="mt-4 grid grid-cols-2 gap-2 rounded-lg bg-muted/40 p-3 text-center">
            <div><dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Vehicles</dt><dd className="mt-0.5 font-semibold">{vehicles.length}</dd></div>
            <div><dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Joined</dt><dd className="mt-0.5 font-semibold">{new Date(person.created_at).toLocaleDateString()}</dd></div>
          </dl>
        </div>

        <Tabs defaultValue="vehicles">
          <TabsList>
            <TabsTrigger value="vehicles"><Truck className="mr-1.5 h-3.5 w-3.5" /> Vehicles</TabsTrigger>
            <TabsTrigger value="activity"><ActivityIcon className="mr-1.5 h-3.5 w-3.5" /> Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="vehicles">
            <div className="surface-card">
              <Table>
                <TableHeader><TableRow><TableHead>Plate</TableHead><TableHead>Make/model</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {vehicles.map((v) => (
                    <TableRow key={v.id} className="cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/app/vehicles/${v.id}`)}>
                      <TableCell className="font-mono text-xs">{v.plate}</TableCell>
                      <TableCell>{[v.make, v.model].filter(Boolean).join(" ") || "—"}</TableCell>
                      <TableCell className="capitalize">{v.status}</TableCell>
                    </TableRow>
                  ))}
                  {vehicles.length === 0 ? <TableRow><TableCell colSpan={3} className="py-6 text-center text-sm text-muted-foreground">No vehicles registered.</TableCell></TableRow> : null}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="activity">
            <div className="surface-card divide-y">
              {activity.map((n) => (
                <div key={n.id} className="p-3">
                  <div className="text-sm font-medium">{n.title}</div>
                  {n.detail ? <div className="text-xs text-muted-foreground">{n.detail}</div> : null}
                  <div className="mt-1 text-[10px] text-muted-foreground">{timeAgo(n.created_at)}</div>
                </div>
              ))}
              {activity.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No activity yet.</div> : null}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
